// EssentialsCard — who the contract is with, what it is, and where it sits in
// its term. The facts a steering reader needs to place everything else on the
// page.
//
// Deliberately carries NO contract value: the value renders abbreviated in
// the band and at full precision in the financial section, and nowhere else.
// A third rendering would give the reader a number to reconcile rather than a
// fact to read.
//
// The term bar renders the server's `termElapsedPct` verbatim. The frontend
// never recomputes it — the same number has to appear on the screen and in
// the PDF, and a second derivation is a second chance to disagree.

import ReportSection, { UnavailableNotice } from "./ReportSection";
import { extensionPeriodSummary } from "../../../lib/contract-overview/extensionPeriodSummary";
import {
  CONTRACT_STATUS_LABEL,
  CONTRACT_STATUS_PILL_CLASS,
} from "../../../lib/contract-overview/contractStatusPill";
import { formatDayMonthYear } from "../../../utils/formatShortDate";
import type {
  SectionUnavailable,
  SummaryEssentialsSection,
} from "../../../types/report";

const SECTION_LABEL = "Contract essentials";

export interface EssentialsCardProps {
  section: SummaryEssentialsSection | SectionUnavailable;
}

export default function EssentialsCard({ section }: EssentialsCardProps) {
  if (section.status === "unavailable") {
    return (
      <ReportSection id="essentials" title={SECTION_LABEL}>
        <UnavailableNotice label={SECTION_LABEL} />
      </ReportSection>
    );
  }

  const extensions = extensionPeriodSummary(section.extensionOptions);

  return (
    <ReportSection id="essentials" title={SECTION_LABEL}>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field testId="supplier" label="Supplier" value={section.supplierName} />
        <div>
          <dt className="text-caption font-medium text-slate-500 dark:text-slate-400">
            Reference
          </dt>
          <dd className="mt-0.5 font-mono text-body-sm text-slate-900 dark:text-slate-100">
            {section.contractNumber}
          </dd>
        </div>
        <Field
          testId="type"
          label="Contract type"
          value={section.contractTypeLabel}
        />
        <div>
          <dt className="text-caption font-medium text-slate-500 dark:text-slate-400">
            Status
          </dt>
          <dd className="mt-0.5">
            <span
              data-testid="essentials-status"
              className={`inline-flex max-w-full items-center truncate rounded-full px-2.5 py-1 text-caption font-medium ${CONTRACT_STATUS_PILL_CLASS[section.contractStatus]}`}
            >
              {CONTRACT_STATUS_LABEL[section.contractStatus]}
            </span>
          </dd>
        </div>
        <Field
          testId="business-unit"
          label="Business unit"
          value={section.businessUnitLabel}
        />
        <Field
          testId="procurement"
          label="Procurement method"
          value={section.procurementMethodLabel}
        />
        <Field
          testId="manager"
          label="Contract manager"
          value={section.managerName}
        />
        <Field testId="owner" label="Contract owner" value={section.ownerName} />
      </dl>

      <div className="mt-5 border-t border-slate-100 pt-4 dark:border-slate-700">
        <p className="text-caption font-medium text-slate-500 dark:text-slate-400">
          Term
        </p>
        <p
          data-testid="essentials-term"
          className="mt-0.5 text-body-sm tabular-nums text-slate-900 dark:text-slate-100"
        >
          {formatDayMonthYear(section.startDate)}{" "}
          <span aria-hidden="true">–</span>{" "}
          {formatDayMonthYear(section.endDate)}
        </p>
        {section.termElapsedPct !== null && (
          <TermElapsedBar percent={section.termElapsedPct} />
        )}
        {extensions && (
          <p className="mt-2 text-caption italic text-slate-500 dark:text-slate-400">
            {extensions}
          </p>
        )}
      </div>
    </ReportSection>
  );
}

/** A labelled fact. A null value renders an em dash rather than an empty
 *  cell, so "not recorded" is visibly different from a layout gap. */
function Field({
  testId,
  label,
  value,
}: {
  testId: string;
  label: string;
  value: string | null;
}) {
  return (
    <div>
      <dt className="text-caption font-medium text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd
        data-testid={`essentials-field-${testId}`}
        className="mt-0.5 text-body-sm text-slate-900 dark:text-slate-100"
      >
        {value === null ? "—" : value}
      </dd>
    </div>
  );
}

function TermElapsedBar({ percent }: { percent: number }) {
  const clamped = Math.min(100, Math.max(0, percent));
  return (
    <div
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Term elapsed: ${Math.round(percent)} percent`}
      className="mt-2 h-1 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700"
    >
      <div
        className="h-full rounded-full bg-brand-600 dark:bg-brand-400"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}
